import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { type ChatAttachmentRef } from '@/lib/api';

interface FileViewerModalProps {
  attachment: ChatAttachmentRef;
  url: string;
  onClose: () => void;
}

export function FileViewerModal({ attachment, url, onClose }: FileViewerModalProps) {
  const isImage = attachment.mimeType.startsWith('image/');
  const isVideo = attachment.mimeType.startsWith('video/');

  // Close on Escape
  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handle, true);
    return () => document.removeEventListener('keydown', handle, true);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center animate-in fade-in-0 duration-150"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80" />

      {/* Content — stop propagation so clicking media doesn't close modal */}
      <div
        className="relative z-10 flex flex-col items-center max-w-[90vw] max-h-[90vh] animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute -top-10 right-0 p-2 text-white/70 hover:text-white transition-colors"
          title="Close (Esc)"
        >
          <X className="h-5 w-5" />
        </button>

        {isImage && (
          <img
            src={url}
            alt={attachment.fileName}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
            draggable={false}
          />
        )}

        {isVideo && (
          <video
            src={url}
            controls
            autoPlay
            className="max-w-[90vw] max-h-[85vh] rounded-lg shadow-2xl"
          />
        )}

        {/* Filename caption */}
        <p className="mt-3 text-sm text-white/60 truncate max-w-[60vw]">{attachment.fileName}</p>
      </div>
    </div>,
    document.body,
  );
}
